<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('vip.adminPurchase') }}</span>
        </div>
      </template>

      <el-form :model="form" :rules="rules" ref="formRef" :label-width="formLabelWidth" :label-position="isMobile ? 'top' : 'right'" size="default">
        <!-- Customer Selection -->
        <el-form-item :label="$t('vip.customer')" prop="customerId">
          <el-select 
            v-model="form.customerId" 
            filterable 
            :placeholder="$t('vip.selectCustomer')"
            @change="onCustomerChange"
            style="width: 100%"
          >
            <el-option
              v-for="customer in customers"
              :key="customer.id"
              :label="`${customer.name} (${customer.phone})`"
              :value="customer.id"
            />
          </el-select>
        </el-form-item>

        <!-- VIP Package Selection -->
        <el-form-item :label="$t('vip.package')" prop="packageId">
          <el-select 
            v-model="form.packageId" 
            filterable 
            :placeholder="$t('vip.selectPackage')"
            style="width: 100%"
          >
            <el-option
              v-for="pkg in vipPackages"
              :key="pkg.id"
              :label="`${pkg.name} - ¥${pkg.price} (${pkg.courseHours}${$t('vip.hoursUnit')}/${pkg.validityDays}${$t('vip.daysUnit')})`"
              :value="pkg.id"
              :disabled="!pkg.isActive"
            />
          </el-select>
        </el-form-item>

        <!-- Note -->
        <el-form-item :label="$t('vip.note')" prop="note">
          <el-input 
            v-model="form.note" 
            type="textarea" 
            :rows="3"
            :placeholder="$t('vip.enterNote')"
          />
        </el-form-item>

        <!-- Form Actions -->
        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="handleSubmitWithPayment">
            {{ $t('vip.purchaseWithPayment') }}
          </el-button>
          <el-button @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>

      <!-- Customer Remaining VIP Hours Display -->
      <div v-if="remainingHoursInfo.hasData || remainingHoursInfo.isLoading" class="remaining-hours-section">
        <el-divider />
        <div class="remaining-hours-card">
          <div class="card-header-icon">
            <el-icon><InfoFilled /></el-icon>
          </div>
          <div class="card-content">
            <div v-if="remainingHoursInfo.isLoading" class="loading-state">
              <el-icon class="is-loading"><Loading /></el-icon>
              <span>{{ $t('common.loading') }}</span>
            </div>
            <template v-else>
              <div class="info-title">{{ $t('vip.remainingHours') }}</div>
              <div class="info-value">
                <span class="hours-number">{{ remainingHoursInfo.data.totalRemainingHours || 0 }}</span>
                <span class="hours-unit">{{ $t('vip.hoursUnit') }}</span>
              </div>
              <div class="info-stats">
                <div class="stat-item">
                  <span class="stat-label">{{ $t('vip.activePackages') }}:</span>
                  <span class="stat-value">{{ remainingHoursInfo.data.activePackageCount || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">{{ $t('vip.lastUpdated') }}:</span>
                  <span class="stat-value mono-text">{{ formatDateTime(remainingHoursInfo.data.lastUpdated) }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Payment QR Code Dialog -->
      <el-dialog 
        v-model="showPaymentDialog" 
        :title="$t('vip.completePayment')"
        width="420px"
        :close-on-click-modal="false"
        :append-to-body="true"
        @close="resetPaymentState"
        center
      >
        <div class="payment-dialog-content">
          <div v-if="loadingQR" class="loading-container">
            <el-icon class="loading-icon"><Loading /></el-icon>
            <span>{{ $t('vip.generatingQR') }}</span>
          </div>
          <div v-else-if="qrCodeImage" class="qr-container">
            <img :src="qrCodeImage" alt="Payment QR Code" class="qr-code" />
            <div class="qr-tip">{{ $t('vip.scanToPay') }}</div>
            <div class="payment-amount">
              <span class="amount-label">{{ $t('vip.amount') }}:</span>
              <span class="amount-value">¥{{ selectedPackage?.price?.toFixed(2) }}</span>
            </div>
          </div>
          <div v-else-if="currentTransaction?.outTradeNo" class="qr-container">
            <div class="no-qr-message">
              <el-icon class="info-icon"><InfoFilled /></el-icon>
              <p>{{ $t('vip.paymentInitiated') }}</p>
              <p class="transaction-info">{{ $t('vip.orderNumber') }}: {{ currentTransaction.outTradeNo }}</p>
              <p class="transaction-info">{{ $t('vip.amount') }}: ¥{{ currentTransaction.amount?.toFixed(2) }}</p>
              <p class="waiting-text">{{ $t('vip.waitingForPayment') }}</p>
            </div>
          </div>
          <div v-else class="error-container">
            <el-icon class="error-icon"><WarningFilled /></el-icon>
            <span>{{ $t('vip.qrFailed') }}</span>
            <el-button type="primary" size="small" @click="generatePaymentQR" class="retry-btn">
              {{ $t('common.retry') }}
            </el-button>
          </div>
          
          <div v-if="paymentSuccess" class="payment-success">
            <el-icon class="success-icon"><CircleCheckFilled /></el-icon>
            <span>{{ $t('vip.paymentSuccess') }}</span>
          </div>
        </div>
        <template #footer>
          <el-button @click="showPaymentDialog = false">{{ $t('common.close') }}</el-button>
        </template>
      </el-dialog>

      <!-- Payment Success Receipt Dialog -->
      <el-dialog 
        v-model="showSuccessDialog" 
        width="380px"
        :close-on-click-modal="false"
        :append-to-body="true"
        :show-close="true"
        class="receipt-dialog"
      >
        <div class="receipt-paper" :class="{ 'animate-in': showSuccessDialog }">
          <!-- Receipt Header -->
          <div class="receipt-header">
            <div class="success-icon-wrapper">
              <div class="checkmark-circle">
                <svg viewBox="0 0 52 52" class="checkmark-svg">
                  <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
                  <path class="checkmark-stroke" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                </svg>
              </div>
            </div>
            <h3 class="receipt-title">{{ $t('vip.paymentSuccess') }}</h3>
            <p class="receipt-subtitle">Payment Successful</p>
          </div>

          <!-- Zigzag Separator -->
          <div class="receipt-separator-top"></div>

          <!-- Receipt Body -->
          <div class="receipt-body">
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('vip.customer') }}</span>
              <span class="receipt-value">{{ receiptData.customerName }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row receipt-row-highlight">
              <span class="receipt-label">{{ $t('common.amount') }}</span>
              <span class="receipt-value receipt-amount">¥{{ receiptData.paidAmount }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('booking.detail.orderNumber') }}</span>
              <span class="receipt-value receipt-mono">{{ receiptData.outTradeNo }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('vip.transactionId') }}</span>
              <span class="receipt-value receipt-mono">{{ receiptData.transactionId }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('vip.purchasedHours') }}</span>
              <span class="receipt-value">{{ receiptData.purchasedHours }}{{ $t('vip.hoursUnit') }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('vip.originalRemainingHours') }}</span>
              <span class="receipt-value">{{ receiptData.originalRemainingHours }}{{ $t('vip.hoursUnit') }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row receipt-row-highlight">
              <span class="receipt-label">{{ $t('vip.totalRemainingHours') }}</span>
              <span class="receipt-value receipt-amount">{{ receiptData.totalRemainingHours }}{{ $t('vip.hoursUnit') }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('common.status') }}</span>
              <el-tag :type="receiptData.isActive ? 'success' : 'info'" size="small">
                {{ receiptData.isActive ? $t('vip.active') : $t('vip.inactive') }}
              </el-tag>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="receipt-row">
              <span class="receipt-label">{{ $t('vip.endTime') }}</span>
              <span class="receipt-value receipt-mono">{{ receiptData.endTime }}</span>
            </div>
          </div>

          <!-- Zigzag Separator -->
          <div class="receipt-separator-bottom"></div>

          <!-- Receipt Footer -->
          <div class="receipt-footer">
            <p class="receipt-thanks">{{ $t('vip.thankYouForPurchase') }}</p>
            <p class="receipt-datetime">{{ receiptData.timestamp }}</p>
          </div>
        </div>
        
        <template #footer>
          <div class="receipt-footer-actions">
            <el-button type="primary" @click="showSuccessDialog = false" class="confirm-btn">
              {{ $t('common.confirm') }}
            </el-button>
          </div>
        </template>
      </el-dialog>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Loading, WarningFilled, CircleCheckFilled, InfoFilled, CircleCloseFilled, SuccessFilled } from '@element-plus/icons-vue'
import { useVipStore } from '@/stores/vip'
import { useCustomerStore } from '@/stores/customer'
import { useAppStore } from '@/stores/app'
import { adminPurchaseWithPayment, adminPurchaseNoPayment, getVipPayStatus, getCustomerRemainingHours } from '@/api/vip'
import QRCode from 'qrcode'
import dayjs from 'dayjs'

const router = useRouter()
const { t } = useI18n()
const vipStore = useVipStore()
const customerStore = useCustomerStore()
const appStore = useAppStore()

const formRef = ref(null)
const form = ref({
  customerId: null,
  packageId: null,
  note: ''
})

const rules = {
  customerId: [
    { required: true, message: t('vip.selectCustomer'), trigger: 'change' }
  ],
  packageId: [
    { required: true, message: t('vip.selectPackage'), trigger: 'change' }
  ]
}

const customers = computed(() => customerStore.customers)
const vipPackages = computed(() => vipStore.vipPackages)
const selectedPackage = computed(() => {
  return vipPackages.value.find(pkg => pkg.id === form.value.packageId)
})
const selectedCustomer = computed(() => {
  return customers.value.find(c => c.id === form.value.customerId)
})

// Responsive form label width
const isMobile = ref(false)
const formLabelWidth = computed(() => {
  return isMobile.value ? undefined : '120px'
})

// Remaining hours state
const remainingHoursInfo = ref({
  isLoading: false,
  hasData: false,
  data: null
})

// Store original remaining hours before payment (for calculation)
const originalRemainingHours = ref(0)

const submitting = ref(false)
const showPaymentDialog = ref(false)
const showSuccessDialog = ref(false)
const loadingQR = ref(false)
const qrCodeUrl = ref('')
const qrCodeImage = ref('') // Base64 QR code image
const currentTransaction = ref(null)
const pollingTimer = ref(null)
const maxPollingAttempts = 60 // 2 minutes maximum (60 attempts * 2 seconds)
let pollingAttempts = 0
const paymentSuccess = ref(false)

// Receipt data
const receiptData = ref({
  customerName: '',
  paidAmount: '',
  outTradeNo: '',
  transactionId: '',
  purchasedHours: '',
  originalRemainingHours: '',
  totalRemainingHours: '',
  isActive: false,
  endTime: '',
  timestamp: ''
})

// Fetch data on mount
onMounted(async () => {
  // Check initial screen size
  checkScreenSize()
  
  await Promise.all([
    customerStore.fetchCustomers(),
    vipStore.fetchVipPackages(true) // Only active packages
  ])
  
  // Add resize listener for responsive label width
  window.addEventListener('resize', checkScreenSize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
})

const checkScreenSize = () => {
  isMobile.value = window.innerWidth <= 768
}

const onCustomerChange = async (customerId) => {
  if (!customerId) {
    remainingHoursInfo.value = {
      isLoading: false,
      hasData: false,
      data: null
    }
    originalRemainingHours.value = 0
    return
  }

  remainingHoursInfo.value.isLoading = true
  
  try {
    const response = await getCustomerRemainingHours(customerId)
    
    if (response.success && response.data !== undefined) {
      remainingHoursInfo.value = {
        isLoading: false,
        hasData: true,
        data: response.data
      }
      // Store the original remaining hours before purchase
      originalRemainingHours.value = response.data.totalRemainingHours || 0
    } else {
      remainingHoursInfo.value = {
        isLoading: false,
        hasData: false,
        data: null
      }
      originalRemainingHours.value = 0
    }
  } catch (error) {
    console.error('Failed to fetch remaining hours:', error)
    remainingHoursInfo.value = {
      isLoading: false,
      hasData: false,
      data: null
    }
    originalRemainingHours.value = 0
  }
}

const handleSubmitWithPayment = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    submitting.value = true
    try {
      const payload = {
        packageId: form.value.packageId,
        customerId: form.value.customerId,
        note: form.value.note || ''
      }
      
      // Check operation mode from club info
      const clubInfo = appStore.clubInfo
      const operationMode = clubInfo?.operationMode ?? 0
      
      if (operationMode === 1) {
        // Operation Mode 1: Use no-payment API (instant result)
        const response = await adminPurchaseNoPayment(payload)
        
        if (response.success && response.data) {
          ElMessage.success(t('vip.purchaseSuccess'))
          
          // Show success receipt dialog with returned data
          const data = response.data
          
          // Use endTime from API response or calculate from startTime
          const endTime = data.endTime ? dayjs(data.endTime) : (data.startTime ? dayjs(data.startTime).add(data.validityDays || 0, 'day') : dayjs().add(data.validityDays || 0, 'day'))
          
          // Populate receipt data
          receiptData.value = {
            customerName: selectedCustomer.value?.name || '-',
            paidAmount: data.paidAmount?.toFixed(2) || '0.00',
            outTradeNo: data.outTradeNo || '-',
            transactionId: data.transactionId || '-',
            purchasedHours: data.totalHours || 0,
            originalRemainingHours: originalRemainingHours.value,
            totalRemainingHours: (originalRemainingHours.value + (data.totalHours || 0)),
            isActive: data.isActive || false,
            endTime: endTime.format('YYYY-MM-DD HH:mm:ss'),
            timestamp: data.purchaseDate ? dayjs(data.purchaseDate).format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss')
          }
          
          // Show success receipt dialog
          showSuccessDialog.value = true
          
          // Reset form after success
          handleReset()
        } else {
          ElMessage.error(response.message || t('vip.purchaseFailed'))
        }
      } else {
        // Operation Mode 0: Use WeChat Pay with QR code and polling
        const response = await adminPurchaseWithPayment(payload)
        
        if (response.success && response.data) {
          // Store transaction data for polling
          currentTransaction.value = {
            customerVipId: response.data.customerVipId,
            outTradeNo: response.data.outTradeNo,
            amount: response.data.amount
          }
          
          // Extract QR code URL from weChatPayParams.CodeUrl
          const extractedQrUrl = response.data.weChatPayParams?.CodeUrl || response.data.qrCodeUrl || ''
          
          // Set QR code URL and generate QR code image
          if (extractedQrUrl) {
            qrCodeUrl.value = extractedQrUrl
            await generateQRCodeImage(extractedQrUrl)
          } else {
            qrCodeUrl.value = ''
            qrCodeImage.value = ''
          }
          
          paymentSuccess.value = false
          loadingQR.value = false
          
          // Show payment QR code dialog
          showPaymentDialog.value = true
          
          // Start polling for payment status
          startPolling()
        } else {
          ElMessage.error(response.message || t('vip.purchaseFailed'))
        }
      }
    } catch (error) {
      console.error('Purchase failed:', error)
      ElMessage.error(error.message || t('vip.purchaseFailed'))
    } finally {
      submitting.value = false
    }
  })
}

const handleReset = () => {
  form.value = {
    customerId: null,
    packageId: null,
    note: ''
  }
  
  // Reset remaining hours data
  remainingHoursInfo.value = {
    isLoading: false,
    hasData: false,
    data: null
  }
  originalRemainingHours.value = 0
  
  if (formRef.value) {
    formRef.value.clearValidate()
  }
}

const generatePaymentQR = async () => {
  loadingQR.value = true
  qrCodeUrl.value = ''
  paymentSuccess.value = false
  
  try {
    const payload = {
      packageId: form.value.packageId,
      customerId: form.value.customerId,
      note: form.value.note || ''
    }
    
    const response = await adminPurchaseWithPayment(payload)
    
    if (response.success && response.data) {
      // Store transaction data for polling
      currentTransaction.value = {
        customerVipId: response.data.customerVipId,
        outTradeNo: response.data.outTradeNo,
        amount: response.data.amount
      }
      
      // Extract QR code URL from weChatPayParams.CodeUrl
      const extractedQrUrl = response.data.weChatPayParams?.CodeUrl || response.data.qrCodeUrl || ''
      
      // Set QR code URL and generate QR code image
      if (extractedQrUrl) {
        qrCodeUrl.value = extractedQrUrl
        await generateQRCodeImage(extractedQrUrl)
      } else {
        qrCodeUrl.value = ''
        qrCodeImage.value = ''
      }
      
      paymentSuccess.value = false
      loadingQR.value = false
      
      startPolling()
    } else {
      ElMessage.error(response.message || t('vip.qrFailed'))
      qrCodeUrl.value = ''
      loadingQR.value = false
    }
  } catch (error) {
    console.error('Failed to generate QR:', error)
    ElMessage.error(t('vip.qrFailed'))
    loadingQR.value = false
  }
}

const startPolling = () => {
  // Stop any existing polling first
  stopPolling()
  
  pollingAttempts = 0
  
  // Poll every 2 seconds to check payment status
  pollingTimer.value = setInterval(async () => {
    pollingAttempts++
    
    // Stop polling after max attempts
    if (pollingAttempts >= maxPollingAttempts) {
      stopPolling()
      ElMessage.warning(t('vip.paymentTimeout'))
      return
    }
    
    try {
      // Check payment status using the transaction data
      if (currentTransaction.value?.outTradeNo) {
        const paymentStatus = await getVipPayStatus(currentTransaction.value.outTradeNo)
        
        if (paymentStatus.success && paymentStatus.data) {
          const status = paymentStatus.data.status?.toUpperCase()
          
          // Payment succeeded
          if (status === 'PAID') {
            stopPolling()
            showPaymentDialog.value = false
            
            const data = paymentStatus.data
            
            // Use endTime from API response
            const endTime = data.endTime ? dayjs(data.endTime) : (data.startTime ? dayjs(data.startTime).add(data.validityDays || 0, 'day') : null)
            
            // Populate receipt data
            receiptData.value = {
              customerName: selectedCustomer.value?.name || '-',
              paidAmount: data.paidAmount?.toFixed(2) || '0.00',
              outTradeNo: data.outTradeNo || '-',
              transactionId: data.transactionId || '-',
              purchasedHours: data.totalHours || 0,
              originalRemainingHours: originalRemainingHours.value,
              totalRemainingHours: (originalRemainingHours.value + (data.totalHours || 0)),
              isActive: data.isActive || false,
              endTime: endTime ? endTime.format('YYYY-MM-DD HH:mm:ss') : '-',
              timestamp: data.purchaseDate ? dayjs(data.purchaseDate).format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss')
            }
            
            // Show success receipt dialog
            showSuccessDialog.value = true
            
            // Reset form after success
            handleReset()
          }
          // Payment failed
          else if (status === 'FAILED' || status === 'CANCELLED' || status === 'REFUND') {
            stopPolling()
            showPaymentDialog.value = false
            
            // Show failure dialog
            await ElMessageBox.alert(
              `<div style="text-align: center; padding: 20px 0;">
                <div style="color: var(--el-color-danger); font-size: 48px; margin-bottom: 16px;">
                  <el-icon><CircleCloseFilled /></el-icon>
                </div>
                <h3 style="margin: 0 0 12px 0; color: var(--el-color-danger);">${t('vip.paymentFailed')}</h3>
                <p style="margin: 0; color: var(--el-text-color-regular);">${paymentStatus.data.message || t('vip.paymentFailed')}</p>
              </div>`,
              t('vip.completePayment'),
              {
                dangerouslyUseHTMLString: true,
                confirmButtonText: t('common.confirm'),
                type: 'error',
                center: true,
                customClass: 'payment-result-dialog'
              }
            )
          }
        }
      }
    } catch (error) {
      console.error('Polling error:', error)
      // Continue polling on error, but log it
    }
  }, 2000)
}

const stopPolling = () => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
    pollingTimer.value = null
  }
}

const handleImageError = () => {
  console.error('QR code image failed to load')
  qrCodeUrl.value = '' // Clear the broken URL to show fallback UI
}

const generateQRCodeImage = async (url) => {
  try {
    qrCodeImage.value = await QRCode.toDataURL(url, {
      width: 280,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    })
  } catch (error) {
    console.error('Failed to generate QR code:', error)
    qrCodeImage.value = ''
  }
}

const isValidUrl = (url) => {
  if (!url) return false
  // WeChat pay URLs like "weixin://wxpay/bizpayurl?pr=xxx" are valid
  return url.includes('weixin://') || url.startsWith('http')
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  try {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss')
  } catch (error) {
    return dateString
  }
}

const resetPaymentState = () => {
  stopPolling()
  currentTransaction.value = null
  qrCodeUrl.value = ''
  qrCodeImage.value = ''
  paymentSuccess.value = false
  loadingQR.value = false
  pollingAttempts = 0
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  transition: background-color 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
}

// Responsive form styles
:deep(.el-form) {
  @media (max-width: 768px) {
    :deep(.el-form-item__label) {
      float: none;
      text-align: left;
      padding-bottom: 8px;
      line-height: 1.4;
      font-size: 14px;
    }
    
    :deep(.el-form-item__content) {
      margin-left: 0 !important;
    }
    
    :deep(.el-select),
    :deep(.el-input) {
      width: 100% !important;
      
      :deep(.el-input__wrapper) {
        border-radius: 6px;
        padding: 10px 12px;
      }
      
      :deep(.el-input__inner) {
        font-size: 14px;
      }
    }
    
    :deep(.el-textarea) {
      :deep(.el-textarea__inner) {
        border-radius: 6px;
        padding: 10px 12px;
        font-size: 14px;
      }
    }
  }
  
  @media (max-width: 480px) {
    :deep(.el-form-item__label) {
      font-size: 13px;
      padding-bottom: 6px;
    }
    
    :deep(.el-select),
    :deep(.el-input) {
      :deep(.el-input__wrapper) {
        padding: 9px 11px;
      }
      
      :deep(.el-input__inner) {
        font-size: 13px;
      }
    }
  }
}

// Form actions responsive
:deep(.el-form-item) {
  &:last-child {
    @media (max-width: 768px) {
      margin-top: 16px;
      margin-left: 0 !important;
      
      .el-form-item__content {
        margin-left: 0 !important;
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
      }
      
      .el-button {
        width: 100% !important;
        margin: 0 !important;
        height: 48px;
        font-size: 15px;
        font-weight: 500;
        border-radius: 8px;
      }
    }
    
    @media (max-width: 480px) {
      .el-button {
        height: 46px;
        font-size: 14px;
      }
    }
  }
}

.payment-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  
  @media (max-width: 768px) {
    padding: 16px 0;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 0;
    
    @media (max-width: 768px) {
      padding: 32px 0;
    }

    .loading-icon {
      font-size: 32px;
      animation: rotating 2s linear infinite;
      color: var(--el-color-primary);
      
      @media (max-width: 768px) {
        font-size: 28px;
      }
    }
  }

  .qr-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 100%;
    
    @media (max-width: 768px) {
      gap: 12px;
    }

    .qr-code {
      width: 280px;
      height: 280px;
      object-fit: contain;
      border-radius: 8px;
      border: 1px solid var(--border-color-light, #e4e7ed);
      transition: border-color 0.3s ease;
      
      @media (max-width: 768px) {
        width: 240px;
        height: 240px;
        border-radius: 6px;
      }
      
      @media (max-width: 480px) {
        width: 200px;
        height: 200px;
        border-radius: 4px;
      }
    }

    .no-qr-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 20px;
      text-align: center;
      width: 100%;
      
      @media (max-width: 768px) {
        padding: 16px;
        gap: 10px;
      }

      .info-icon {
        font-size: 48px;
        color: var(--el-color-primary);
        
        @media (max-width: 768px) {
          font-size: 40px;
        }
      }

      p {
        margin: 0;
        font-size: 14px;
        color: var(--color-text-regular, #606266);
        transition: color 0.3s ease;
        
        @media (max-width: 768px) {
          font-size: 13px;
        }
      }

      .transaction-info {
        font-size: 13px;
        color: var(--color-text-secondary, #909399);
        font-family: monospace;
        word-break: break-all;
        
        @media (max-width: 768px) {
          font-size: 12px;
        }
      }

      .waiting-text {
        font-size: 15px;
        font-weight: 500;
        color: var(--el-color-warning);
        margin-top: 8px;
        
        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }

    .qr-tip {
      font-size: 14px;
      color: var(--color-text-secondary, #909399);
      transition: color 0.3s ease;
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
    }

    .payment-amount {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      background: var(--bg-color-base, #f5f7fa);
      border-radius: 4px;
      width: 100%;
      justify-content: center;
      
      @media (max-width: 768px) {
        padding: 10px 16px;
        border-radius: 6px;
      }

      .amount-label {
        font-size: 14px;
        color: var(--color-text-regular, #606266);
        transition: color 0.3s ease;
        
        @media (max-width: 768px) {
          font-size: 13px;
        }
      }

      .amount-value {
        font-size: 20px;
        font-weight: 600;
        color: var(--el-color-primary);
        transition: color 0.3s ease;
        
        @media (max-width: 768px) {
          font-size: 18px;
        }
      }
    }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 0;
    
    @media (max-width: 768px) {
      padding: 32px 0;
      gap: 10px;
    }

    .error-icon {
      font-size: 32px;
      color: var(--el-color-danger);
      
      @media (max-width: 768px) {
        font-size: 28px;
      }
    }

    .retry-btn {
      margin-top: 8px;
      min-height: 40px;
      
      @media (max-width: 768px) {
        margin-top: 6px;
      }
    }
  }

  .payment-success {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    background: var(--bg-color-base, #f5f7fa);
    border-radius: 4px;
    margin-top: 16px;
    width: 100%;
    justify-content: center;
    
    @media (max-width: 768px) {
      padding: 14px;
      margin-top: 12px;
      border-radius: 6px;
    }

    .success-icon {
      font-size: 24px;
      color: var(--el-color-success);
      
      @media (max-width: 768px) {
        font-size: 20px;
      }
    }

    span {
      font-size: 14px;
      font-weight: 500;
      color: var(--el-color-success);
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Remaining Hours Section */
.remaining-hours-section {
  margin-top: 24px;
  animation: slideIn 0.3s ease-out;
  
  @media (max-width: 768px) {
    margin-top: 16px;
  }
  
  :deep(.el-divider) {
    margin: 16px 0;
    
    @media (max-width: 768px) {
      margin: 12px 0;
    }
  }
}

.remaining-hours-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(103, 194, 58, 0.08) 0%, rgba(103, 194, 58, 0.03) 100%);
  border: 1px solid rgba(103, 194, 58, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(103, 194, 58, 0.15);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    gap: 10px;
    border-radius: 4px;
  }
  
  .card-header-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(103, 194, 58, 0.15);
    border-radius: 50%;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
    }
    
    .el-icon {
      font-size: 20px;
      color: var(--el-color-success);
      
      @media (max-width: 768px) {
        font-size: 18px;
      }
    }
  }
  
  .card-content {
    flex: 1;
    min-width: 0;
    
    .loading-state {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--el-text-color-secondary);
      font-size: 14px;
      justify-content: center;
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
      
      .el-icon {
        font-size: 16px;
        
        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }
    
    .info-title {
      font-size: 13px;
      color: var(--el-text-color-secondary);
      margin-bottom: 4px;
      
      @media (max-width: 768px) {
        font-size: 12px;
      }
    }
    
    .info-value {
      display: flex;
      align-items: baseline;
      gap: 4px;
      margin-bottom: 8px;
      justify-content: center;
      
      @media (max-width: 768px) {
        margin-bottom: 6px;
      }
      
      .hours-number {
        font-size: 24px;
        font-weight: 700;
        color: var(--el-color-success);
        
        @media (max-width: 768px) {
          font-size: 20px;
        }
        
        @media (max-width: 480px) {
          font-size: 18px;
        }
      }
      
      .hours-unit {
        font-size: 14px;
        color: var(--el-text-color-secondary);
        font-weight: 500;
        
        @media (max-width: 768px) {
          font-size: 13px;
        }
      }
    }
    
    .info-stats {
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      @media (max-width: 768px) {
        gap: 3px;
      }
      
      .stat-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        
        @media (max-width: 768px) {
          font-size: 11px;
          justify-content: center;
          gap: 8px;
        }
        
        .stat-label {
          color: var(--el-text-color-secondary);
          font-weight: 500;
        }
        
        .stat-value {
          color: var(--el-text-color-primary);
          font-weight: 600;
          
          &.mono-text {
            font-family: 'Courier New', monospace;
            font-size: 11px;
            
            @media (max-width: 768px) {
              font-size: 10px;
            }
          }
        }
      }
    }
  }
}

/* Payment Receipt Dialog Styles */
:deep(.receipt-dialog) {
  @media (max-width: 768px) {
    width: 90% !important;
    max-width: 360px !important;
  }
  
  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }
  
  .el-dialog__body {
    padding: 0;
    background: #f5f5f5;
  }
  
  .el-dialog__footer {
    padding: 16px 20px;
    border-top: 1px solid var(--el-border-color-lighter);
    background: #f5f5f5;
    
    @media (max-width: 768px) {
      padding: 14px 16px;
    }
  }
}

/* Receipt Paper Container */
.receipt-paper {
  width: 100%;
  max-width: 340px;
  margin: 0 auto;
  background: #fff;
  padding: 24px 20px 20px;
  position: relative;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 20px 16px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 14px 14px;
  }
  
  &.animate-in {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Receipt Header */
.receipt-header {
  text-align: center;
  padding-bottom: 20px;
  
  @media (max-width: 768px) {
    padding-bottom: 16px;
  }
  
  .success-icon-wrapper {
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    position: relative;
    
    @media (max-width: 768px) {
      width: 56px;
      height: 56px;
      margin-bottom: 12px;
    }
    
    @media (max-width: 480px) {
      width: 48px;
      height: 48px;
      margin-bottom: 10px;
    }
  }
  
  .checkmark-circle {
    width: 100%;
    height: 100%;
    background: var(--el-color-success);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: scaleIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    
    .checkmark-svg {
      width: 32px;
      height: 32px;
      
      @media (max-width: 768px) {
        width: 28px;
        height: 28px;
      }
      
      @media (max-width: 480px) {
        width: 24px;
        height: 24px;
      }
    }
    
    .checkmark-circle-bg {
      stroke: rgba(255, 255, 255, 0.3);
      stroke-width: 2;
    }
    
    .checkmark-stroke {
      stroke: #fff;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 48;
      stroke-dashoffset: 48;
      animation: strokeDraw 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.3s forwards;
    }
  }
  
  .receipt-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--el-text-color-primary);
    margin: 0 0 4px 0;
    
    @media (max-width: 768px) {
      font-size: 18px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
  
  .receipt-subtitle {
    font-size: 12px;
    color: var(--el-text-color-secondary);
    margin: 0;
    letter-spacing: 0.5px;
    
    @media (max-width: 768px) {
      font-size: 11px;
    }
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes strokeDraw {
  to {
    stroke-dashoffset: 0;
  }
}

/* Zigzag Separators */
.receipt-separator-top,
.receipt-separator-bottom {
  width: 100%;
  height: 12px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 10px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: 
      linear-gradient(45deg, transparent 33.33%, #fff 33.33%, #fff 66.66%, transparent 66.66%),
      linear-gradient(-45deg, transparent 33.33%, #fff 33.33%, #fff 66.66%, transparent 66.66%);
    background-size: 12px 12px;
    
    @media (max-width: 768px) {
      background-size: 10px 10px;
    }
  }
}

/* Receipt Body */
.receipt-body {
  padding: 16px 0;
  
  @media (max-width: 768px) {
    padding: 12px 0;
  }
}

.receipt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  min-height: 36px;
  
  @media (max-width: 768px) {
    padding: 8px 0;
    min-height: 32px;
  }
  
  &.receipt-row-highlight {
    background: linear-gradient(90deg, rgba(103, 194, 58, 0.05) 0%, rgba(103, 194, 58, 0.02) 100%);
    margin: 0 -20px;
    padding: 12px 20px;
    border-radius: 4px;
    
    @media (max-width: 768px) {
      margin: 0 -16px;
      padding: 10px 16px;
    }
    
    @media (max-width: 480px) {
      margin: 0 -14px;
      padding: 8px 14px;
    }
  }
}

.receipt-label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  font-weight: 500;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
}

.receipt-value {
  font-size: 14px;
  color: var(--el-text-color-primary);
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
  
  &.receipt-amount {
    font-size: 22px;
    color: var(--el-color-success);
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 20px;
    }
    
    @media (max-width: 480px) {
      font-size: 18px;
    }
  }
  
  &.receipt-mono {
    font-family: 'Courier New', Consolas, monospace;
    font-size: 12px;
    letter-spacing: 0.3px;
    
    @media (max-width: 768px) {
      font-size: 11px;
    }
    
    @media (max-width: 480px) {
      font-size: 10px;
    }
  }
}

.receipt-divider {
  height: 1px;
  background: repeating-linear-gradient(
    90deg,
    var(--el-border-color-lighter) 0px,
    var(--el-border-color-lighter) 8px,
    transparent 8px,
    transparent 12px
  );
  margin: 4px 0;
}

/* Receipt Footer */
.receipt-footer {
  text-align: center;
  padding-top: 16px;
  border-top: 1px dashed var(--el-border-color);
  
  @media (max-width: 768px) {
    padding-top: 12px;
  }
  
  .receipt-thanks {
    font-size: 13px;
    color: var(--el-text-color-regular);
    margin: 0 0 6px 0;
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 12px;
      margin-bottom: 4px;
    }
  }
  
  .receipt-datetime {
    font-size: 11px;
    color: var(--el-text-color-secondary);
    margin: 0;
    font-family: 'Courier New', Consolas, monospace;
    
    @media (max-width: 768px) {
      font-size: 10px;
    }
  }
}

.receipt-footer-actions {
  display: flex;
  justify-content: center;
  
  .confirm-btn {
    min-width: 120px;
    height: 36px;
    font-size: 14px;
    
    @media (max-width: 768px) {
      width: 100%;
      max-width: 200px;
      height: 40px;
      font-size: 15px;
    }
  }
}

/* Payment Success Dialog Styles */
:deep(.payment-result-dialog) {
  @media (max-width: 768px) {
    width: 90% !important;
    max-width: 340px !important;
  }
  
  .el-message-box__content {
    padding: 32px 20px 20px;
    
    @media (max-width: 768px) {
      padding: 24px 16px 16px;
    }
  }
  
  .el-message-box__message {
    margin: 0;
    padding: 0;
  }
}

// Responsive payment dialog
:deep(.el-dialog) {
  @media (max-width: 768px) {
    width: 90% !important;
    max-width: 400px !important;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    width: 95% !important;
    max-width: 360px !important;
    border-radius: 6px;
  }
}
</style>
